import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SplitIoService } from './external-tools/split-io.service';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService implements OnDestroy {
  private flagCache = new Map<string, BehaviorSubject<boolean>>();
  private loadingPromises = new Map<string, Promise<boolean>>();

  constructor(private splitIoService: SplitIoService) {}

  ngOnDestroy(): void {
    this.flagCache.forEach((subject) => subject.complete());
    this.flagCache.clear();
    this.loadingPromises.clear();
  }

  initPreAuth(): void {
    this.splitIoService.initSplitPreAuth();
  }

  async isFlagEnabled(flagKey: string): Promise<boolean> {
    if (this.flagCache.has(flagKey)) {
      return this.flagCache.get(flagKey)!.value;
    }

    if (this.loadingPromises.has(flagKey)) {
      return this.loadingPromises.get(flagKey)!;
    }

    return this.loadFlag(flagKey);
  }

  getFlag(flagKey: string, forceRefresh: boolean = false): Observable<boolean> {
    if (forceRefresh) {
      return from(this.refreshFlag(flagKey)).pipe(
        switchMap(() => this.flagCache.get(flagKey)!.asObservable())
      );
    }

    if (this.flagCache.has(flagKey)) {
      return this.flagCache.get(flagKey)!.asObservable();
    }

    if (this.loadingPromises.has(flagKey)) {
      return from(this.loadingPromises.get(flagKey)!).pipe(
        switchMap(() => this.flagCache.get(flagKey)!.asObservable())
      );
    }

    return from(this.loadFlag(flagKey)).pipe(
      switchMap(() => this.flagCache.get(flagKey)!.asObservable())
    );
  }

  private async loadFlag(flagKey: string): Promise<boolean> {
    const loadingPromise = this.performFlagLoad(flagKey);
    this.loadingPromises.set(flagKey, loadingPromise);

    try {
      const result = await loadingPromise;
      return result;
    } finally {
      this.loadingPromises.delete(flagKey);
    }
  }

  private async performFlagLoad(flagKey: string): Promise<boolean> {
    try {
      const treatment = await this.splitIoService.getTreatment(flagKey);
      const isEnabled = treatment === 'on';

      this.flagCache.set(flagKey, new BehaviorSubject<boolean>(isEnabled));

      return isEnabled;
    } catch (error) {
      console.error(`Error loading flag ${flagKey}:`, error);
      this.flagCache.set(flagKey, new BehaviorSubject<boolean>(false));
      return false;
    }
  }

  async refreshFlag(flagKey: string): Promise<boolean> {
    try {
      const treatment = await this.splitIoService.getTreatment(flagKey);
      const isEnabled = treatment === 'on';

      if (this.flagCache.has(flagKey)) {
        const currentValue = this.flagCache.get(flagKey)!.value;
        if (currentValue !== isEnabled) {
          this.flagCache.get(flagKey)!.next(isEnabled);
        }
      } else {
        this.flagCache.set(flagKey, new BehaviorSubject<boolean>(isEnabled));
      }

      return isEnabled;
    } catch (error) {
      console.error(`Error refreshing flag ${flagKey}:`, error);
      return this.flagCache.get(flagKey)?.value ?? false;
    }
  }

  isFlagLoaded(flagKey: string): boolean {
    return this.flagCache.has(flagKey);
  }

  clearFlagCache(flagKey: string): void {
    if (this.flagCache.has(flagKey)) {
      this.flagCache.get(flagKey)!.complete();
      this.flagCache.delete(flagKey);
    }
  }

  clearAllFlagsCache(): void {
    this.flagCache.forEach((subject) => subject.complete());
    this.flagCache.clear();
  }
}
